// Time-zone helpers for the clock widget: the wall-clock time in a zone, its UTC offset
// (which changes with summer time) and a readable place name.

/** The device's own zone, e.g. "Europe/London". */
export const deviceZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

function parts(date: Date, tz: string | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone: tz || undefined,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return out;
}

/** Hours (0–23), minutes and seconds on the wall clock in `tz` ('' = this device). */
export function zonedHMS(date: Date, tz: string | undefined): { h: number; m: number; s: number } {
  const p = parts(date, tz);
  return { h: (p.hour ?? 0) % 24, m: p.minute ?? 0, s: p.second ?? 0 };
}

/** Minutes ahead of UTC in `tz` at `date` (e.g. 60 for London in summer, -240 for New York). */
export function utcOffsetMinutes(date: Date, tz: string | undefined): number {
  const p = parts(date, tz);
  const asUtc = Date.UTC(p.year ?? 1970, (p.month ?? 1) - 1, p.day ?? 1, (p.hour ?? 0) % 24, p.minute ?? 0, p.second ?? 0);
  return Math.round((asUtc - Math.floor(date.getTime() / 1000) * 1000) / 60_000);
}

/** 0 → "UTC", 60 → "UTC+1", 330 → "UTC+5:30", -240 → "UTC−4". */
export function formatOffset(minutes: number): string {
  if (minutes === 0) return 'UTC';
  const sign = minutes > 0 ? '+' : '−';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

/** "America/New_York" → "New York"; "America/Argentina/Buenos_Aires" → "Buenos Aires". */
export function zoneCity(tz: string): string {
  if (!tz || tz === 'UTC' || tz === 'Etc/UTC') return 'UTC';
  return (tz.split('/').pop() ?? tz).replace(/_/g, ' ');
}
